/**
 * FusionChatView — Full AI agent chat for Fusion 360 palette.
 * Connects to fusion-agent edge function + fusionBridge for tool execution.
 */

import { useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import fusionBridge from '@/lib/fusionBridge';
import { ChevronDown, ChevronRight, Loader2, Send, Wifi, WifiOff, Paperclip, Image, Menu, Plus, AlertTriangle, Camera } from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  toolCalls?: ToolCallCard[];
}

interface ToolCallCard {
  id: string;
  tool: string;
  params: Record<string, unknown>;
  status: 'pending' | 'running' | 'done' | 'error';
  result?: unknown;
  error?: string;
}

interface ConversationSummary {
  id: string;
  title: string;
  updated_at: string;
}

type FusionModel = 'auto' | 'anthropic/claude-haiku-4-5' | 'google/gemini-2.5-flash' |
  'anthropic/claude-sonnet-4-6' | 'anthropic/claude-opus-4-7' | 'openai/gpt-5.5' | 'google/gemini-3.1-pro-preview';

const MODEL_LABELS: Record<string, string> = {
  'auto': 'Auto',
  'anthropic/claude-haiku-4-5': 'Haiku 4.5',
  'google/gemini-2.5-flash': 'Gemini Flash',
  'anthropic/claude-sonnet-4-6': 'Sonnet 4.6',
  'anthropic/claude-opus-4-7': 'Opus 4.7',
  'openai/gpt-5.5': 'GPT-5.5',
  'google/gemini-3.1-pro-preview': 'Gemini 3.1 Pro',
};

const MODEL_CONTEXT_WINDOWS: Record<string, number> = {
  'auto': 1_000_000,
  'anthropic/claude-haiku-4-5': 200_000,
  'google/gemini-2.5-flash': 1_000_000,
  'anthropic/claude-sonnet-4-6': 200_000,
  'anthropic/claude-opus-4-7': 200_000,
  'openai/gpt-5.5': 128_000,
  'google/gemini-3.1-pro-preview': 2_000_000,
};

const STARTER_PROMPTS = [
  { icon: '🔍', text: 'Analyze my design and suggest improvements' },
  { icon: '📐', text: 'Extract hardcoded dimensions to parameters' },
  { icon: '✨', text: 'Create a parametric version of the selected body' },
];

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function FusionChatView() {
  const { user, isLoading: authLoading } = useAuth();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [model, setModel] = useState<FusionModel>('auto');
  const [modelUsed, setModelUsed] = useState<string | null>(null);
  const [autoTier, setAutoTier] = useState<string | null>(null);
  const [bridgeConnected, setBridgeConnected] = useState(false);
  const [tokenUsage, setTokenUsage] = useState({ input: 0, output: 0, total: 0 });
  const [showModelPicker, setShowModelPicker] = useState(false);
  const [selection, setSelection] = useState<Record<string, unknown>[] | null>(null);
  const [attachments, setAttachments] = useState<string[]>([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [conversationList, setConversationList] = useState<ConversationSummary[]>([]);
  const [credits, setCredits] = useState<number | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Compute active context window based on model actually used
  const activeModel = modelUsed ?? (model === 'auto' ? 'auto' : model);
  const contextWindow = MODEL_CONTEXT_WINDOWS[activeModel] ?? 1_000_000;

  // ── Fusion palette setup ───────────────────────────────────────────────
  useEffect(() => {
    document.documentElement.classList.add('fusion-palette');
    fusionBridge.init();

    const checkBridge = async () => {
      const ok = await fusionBridge.ping(2000);
      setBridgeConnected(ok);
    };
    checkBridge();
    const interval = setInterval(checkBridge, 10000);

    // Re-ping React state immediately when window.adsk is detected by the poll
    const onAdskReady = () => checkBridge();
    window.addEventListener('fusion_adsk_ready', onAdskReady);

    const unsub = fusionBridge.onFusionEvent((event, data) => {
      if (event === 'selection_changed') {
        setSelection((data.selections as Record<string, unknown>[]) ?? null);
      }
    });

    return () => {
      document.documentElement.classList.remove('fusion-palette');
      clearInterval(interval);
      window.removeEventListener('fusion_adsk_ready', onAdskReady);
      unsub();
    };
  }, []);

  // ── Load user credits (shared billing pool) ──────────────────────────────
  const fetchCredits = useCallback(async () => {
    if (!user) return;
    // Get tier's tokens_per_cycle
    const { data: subRow } = await (supabase as any)
      .from('user_subscriptions')
      .select('tier_id, billing_tiers(tokens_per_cycle)')
      .eq('user_id', user.id)
      .single();
    const tokensPerCycle = subRow?.billing_tiers?.tokens_per_cycle ?? 5;

    // Count tokens already used
    const { data: usageRows } = await (supabase as any)
      .from('token_usage_logs')
      .select('tokens_used')
      .eq('user_id', user.id);
    const tokensUsed = (usageRows ?? []).reduce(
      (sum: number, r: { tokens_used: number }) => sum + (r.tokens_used ?? 0), 0
    );
    setCredits(Math.max(0, tokensPerCycle - tokensUsed));
  }, [user]);

  useEffect(() => { fetchCredits(); }, [fetchCredits]);

  // ── Auto-scroll ────────────────────────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── Load conversation list ─────────────────────────────────────────────
  const loadConversationList = useCallback(async () => {
    if (!user) return;
    const { data } = await (supabase as any)
      .from('fusion_conversations')
      .select('id, title, updated_at')
      .eq('user_id', user.id)
      .order('updated_at', { ascending: false })
      .limit(50);
    if (data) setConversationList(data as ConversationSummary[]);
  }, [user]);

  useEffect(() => {
    loadConversationList();
  }, [loadConversationList]);

  // ── Load a conversation ────────────────────────────────────────────────
  const loadConversation = useCallback(async (id: string) => {
    const { data } = await (supabase as any)
      .from('fusion_conversations')
      .select('*')
      .eq('id', id)
      .single();
    if (data) {
      const row = data as { id: string; messages: ChatMessage[]; model: string };
      setConversationId(row.id);
      setMessages(row.messages ?? []);
      setModel((row.model as FusionModel) ?? 'auto');
      setTokenUsage({ input: 0, output: 0, total: 0 });
      setModelUsed(null);
      setAutoTier(null);
    }
    setSidebarOpen(false);
  }, []);

  // ── Save conversation after streaming ──────────────────────────────────
  useEffect(() => {
    if (isStreaming || messages.length === 0 || !user) return;

    const saveConversation = async () => {
      const title = messages[0]?.content?.slice(0, 60) || 'New Chat';
      const payload = {
        user_id: user.id,
        title,
        messages: JSON.parse(JSON.stringify(messages)),
        model,
        updated_at: new Date().toISOString(),
      };

      if (conversationId) {
        await (supabase as any)
          .from('fusion_conversations')
          .update(payload)
          .eq('id', conversationId);
      } else {
        const { data } = await (supabase as any)
          .from('fusion_conversations')
          .insert(payload)
          .select('id')
          .single();
        if (data) setConversationId((data as { id: string }).id);
      }
      loadConversationList();
    };

    saveConversation();
  }, [isStreaming]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── New chat ────────────────────────────────────────────────────────────
  const startNewChat = useCallback(() => {
    setMessages([]);
    setConversationId(null);
    setTokenUsage({ input: 0, output: 0, total: 0 });
    setModelUsed(null);
    setAutoTier(null);
    setAttachments([]);
    setSidebarOpen(false);
  }, []);

  // ── Send prompt ────────────────────────────────────────────────────────
  const sendPrompt = useCallback(async (promptText: string) => {
    if (!promptText.trim() || isStreaming) return;

    const userMsg: ChatMessage = { role: 'user', content: promptText.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsStreaming(true);
    setModelUsed(null);
    setAutoTier(null);

    const assistantMsg: ChatMessage = { role: 'assistant', content: '', toolCalls: [] };
    setMessages(prev => [...prev, assistantMsg]);

    try {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      if (!token) throw new Error('Not authenticated');

      // Get model summary if bridge is connected
      let modelSummary = null;
      if (bridgeConnected || fusionBridge.connected) {
        try {
          modelSummary = await fusionBridge.callTool('get_model_summary');
        } catch { /* ignore */ }
      }

      const historyForApi = messages.map(m => ({ role: m.role, content: m.content }));

      // ── Tool call loop ──────────────────────────────────────────────────
      // AI may request multiple sequential tool calls. After each batch we
      // send results back so it can continue (e.g. sketch → rect → extrude).
      const MAX_ROUNDS = 10;
      let isFirstRound = true;

      for (let round = 0; round < MAX_ROUNDS; round++) {
        abortRef.current = new AbortController();

        const body: Record<string, unknown> = {
          messages: historyForApi,
          model,
        };

        if (isFirstRound) {
          body.prompt = promptText.trim();
          body.modelSummary = modelSummary;
          body.attachments = attachments.length > 0 ? [...attachments] : undefined;
        }
        // Continuation rounds: everything is already in historyForApi

        const res = await fetch(`${SUPABASE_URL}/functions/v1/fusion-agent`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
          signal: abortRef.current.signal,
        });

        if (isFirstRound) {
          setAttachments([]);
          // Add the user prompt to history so continuations include it
          let userContent = promptText.trim();
          if (modelSummary) {
            userContent = `[Current design: ${JSON.stringify(modelSummary)}]\n\n${userContent}`;
          }
          historyForApi.push({ role: 'user', content: userContent });
          isFirstRound = false;
        }

        if (!res.ok) {
          const errText = await res.text();
          throw new Error(`Agent error: ${res.status} — ${errText}`);
        }

        // Parse SSE stream
        const reader = res.body!.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let roundText = '';
        const pendingTools: Array<{ id: string; tool: string; params: Record<string, unknown> }> = [];

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n\n');
          buffer = lines.pop() ?? '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              const event = JSON.parse(line.slice(6));
              switch (event.type) {
                case 'text':
                  roundText += event.content;
                  setMessages(prev => {
                    const updated = [...prev];
                    const last = updated[updated.length - 1];
                    if (last?.role === 'assistant') last.content += event.content;
                    return [...updated];
                  });
                  break;
                case 'model_used':
                  setModelUsed(event.model);
                  setAutoTier(event.autoTier ?? null);
                  break;
                case 'tool_request':
                  pendingTools.push({ id: event.id, tool: event.tool, params: event.params });
                  setMessages(prev => {
                    const updated = [...prev];
                    const last = updated[updated.length - 1];
                    if (last?.role === 'assistant') {
                      last.toolCalls = [...(last.toolCalls ?? []), {
                        id: event.id, tool: event.tool, params: event.params, status: 'running' as const,
                      }];
                    }
                    return [...updated];
                  });
                  break;
                case 'token_usage':
                  setTokenUsage(prev => ({
                    input: prev.input + (event.input ?? 0),
                    output: prev.output + (event.output ?? 0),
                    total: prev.total + (event.total ?? 0),
                  }));
                  break;
                case 'error':
                  setMessages(prev => {
                    const updated = [...prev];
                    const last = updated[updated.length - 1];
                    if (last?.role === 'assistant') last.content += `\n\n⚠️ ${event.message}`;
                    return [...updated];
                  });
                  break;
                case 'credits_update':
                  setCredits(event.balance ?? 0);
                  break;
              }
            } catch { /* skip */ }
          }
        }

        // ── Execute pending tool calls ──────────────────────────────────
        if (pendingTools.length === 0) {
          // No tool calls — AI finished with text. Add to history and exit.
          if (roundText) {
            historyForApi.push({ role: 'assistant', content: roundText });
          }
          break;
        }

        const results: Array<{ tool_call_id: string; tool: string; result: unknown; error?: string }> = [];

        for (const tc of pendingTools) {
          if (bridgeConnected || fusionBridge.connected) {
            try {
              // Log Python scripts for transparency but don't block the workflow
              if (tc.tool === 'run_python_script') {
                console.log('[Gienox] Executing Python script:', (tc.params as any).code);
              }
              const result = await fusionBridge.callTool(tc.tool, tc.params);
              setMessages(prev => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                const card = last?.toolCalls?.find(t => t.id === tc.id);
                if (card) { card.status = 'done'; card.result = result; }
                return [...updated];
              });
              results.push({ tool_call_id: tc.id, tool: tc.tool, result });
            } catch (err) {
              const msg = String(err);
              setMessages(prev => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                const card = last?.toolCalls?.find(t => t.id === tc.id);
                if (card) { card.status = 'error'; card.error = msg; }
                return [...updated];
              });
              results.push({ tool_call_id: tc.id, tool: tc.tool, result: null, error: msg });
            }
          } else {
            const msg = 'Not connected to Fusion 360.';
            setMessages(prev => {
              const updated = [...prev];
              const last = updated[updated.length - 1];
              const card = last?.toolCalls?.find(t => t.id === tc.id);
              if (card) { card.status = 'error'; card.error = msg; }
              return [...updated];
            });
            results.push({ tool_call_id: tc.id, tool: tc.tool, result: null, error: msg });
          }
        }

        // Append to historyForApi using proper OpenAI tool call format.
        // The AI REQUIRES: assistant message with tool_calls[] + tool messages with tool_call_id.
        // Text-based summaries break the tool call loop.
        const assistantToolCallMsg: Record<string, unknown> = {
          role: 'assistant',
          content: roundText || null,
          tool_calls: pendingTools.map(tc => ({
            id: tc.id,
            type: 'function',
            function: {
              name: tc.tool,
              arguments: JSON.stringify(tc.params),
            },
          })),
        };
        historyForApi.push(assistantToolCallMsg as any);

        for (const r of results) {
          historyForApi.push({
            role: 'tool',
            tool_call_id: r.tool_call_id,
            content: JSON.stringify(r.result ?? r.error),
          } as any);
        }
      }
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        setMessages(prev => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last?.role === 'assistant') {
            last.content = `⚠️ ${(err as Error).message}`;
          }
          return [...updated];
        });
      }
    } finally {
      setIsStreaming(false);
      abortRef.current = null;
    }
  }, [isStreaming, messages, model, bridgeConnected]);

  // ── Auth gate ──────────────────────────────────────────────────────────
  if (authLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-adam-bg-secondary-dark">
        <Loader2 className="h-6 w-6 animate-spin text-adam-blue" />
      </div>
    );
  }

  if (!user) {
    const autodeskLoginUrl = `${SUPABASE_URL}/functions/v1/autodesk-auth?action=login`;
    return (
      <div className="flex h-screen w-full flex-col bg-adam-bg-secondary-dark">
        <div className="flex h-8 shrink-0 items-center justify-between border-b border-adam-neutral-800 bg-adam-bg-dark px-3">
          <span className="text-[11px] font-semibold tracking-wider text-adam-neutral-400">GIENOX</span>
          <span className="text-[10px] text-adam-neutral-600">Fusion 360</span>
        </div>
        <div className="mx-auto flex w-full max-w-sm flex-1 flex-col items-center justify-center px-4">
          <img src="/Gienox-Logo.png" alt="Gienox" className="mb-6 w-28" />
          <h2 className="mb-1 text-lg font-semibold text-adam-text-primary">Sign in to Gienox</h2>
          <p className="mb-6 text-center text-xs text-adam-neutral-400">
            AI copilot for Autodesk Fusion 360
          </p>
          <a href={autodeskLoginUrl} className="w-full">
            <button className="flex w-full items-center justify-center gap-2.5 rounded-lg bg-[#1858A8] px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-[#14478a]">
              Sign in with Autodesk
            </button>
          </a>
          <p className="mt-4 text-center text-[10px] text-adam-neutral-600">
            By signing in, you agree to our{' '}
            <a href="/terms-of-service" className="underline hover:text-adam-neutral-400">Terms</a>{' '}and{' '}
            <a href="/privacy-policy" className="underline hover:text-adam-neutral-400">Privacy Policy</a>.
          </p>
        </div>
      </div>
    );
  }

  // ── Main chat UI ───────────────────────────────────────────────────────
  const isEmpty = messages.length === 0;

  return (
    <div className="flex h-screen w-full flex-row bg-adam-bg-secondary-dark text-adam-text-primary">
      {/* Sidebar */}
      {sidebarOpen && (
        <div className="flex h-full w-64 shrink-0 flex-col border-r border-adam-neutral-800 bg-adam-bg-dark">
          <div className="flex items-center justify-between border-b border-adam-neutral-800 px-3 py-2">
            <span className="text-xs font-semibold text-adam-neutral-400">Chat History</span>
            <button onClick={startNewChat} className="flex items-center gap-1 rounded px-2 py-1 text-[10px] text-adam-blue hover:bg-adam-neutral-800">
              <Plus className="h-3 w-3" /> New
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {conversationList.length === 0 ? (
              <p className="px-3 py-4 text-center text-[11px] text-adam-neutral-600">No conversations yet</p>
            ) : (
              conversationList.map(c => (
                <button
                  key={c.id}
                  onClick={() => loadConversation(c.id)}
                  className={`w-full border-b border-adam-neutral-800/50 px-3 py-2.5 text-left transition-colors hover:bg-adam-neutral-800/50 ${
                    c.id === conversationId ? 'bg-adam-neutral-800/70 text-adam-text-primary' : 'text-adam-neutral-400'
                  }`}
                >
                  <p className="truncate text-xs">{c.title}</p>
                  <p className="mt-0.5 text-[9px] text-adam-neutral-600">
                    {new Date(c.updated_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </p>
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {/* Main panel */}
      <div className="flex min-w-0 flex-1 flex-col">
      {/* Bridge status banner */}
      {!bridgeConnected && (
        <div className="flex shrink-0 items-center gap-2 bg-amber-600/90 px-3 py-1.5 text-xs text-white">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          <span>Bridge: Palette bridge unavailable. This page is not loaded inside a Fusion palette. Open it from the Gienox Fusion add-in.</span>
        </div>
      )}

      {/* Top bar */}
      <div className="flex h-10 shrink-0 items-center justify-between border-b border-adam-neutral-800 bg-adam-bg-dark px-3">
        <div className="flex items-center gap-2">
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="rounded p-1 text-adam-neutral-400 hover:bg-adam-neutral-800 hover:text-adam-neutral-200">
            <Menu className="h-4 w-4" />
          </button>
          <button onClick={startNewChat} className="rounded p-1 text-adam-neutral-400 hover:bg-adam-neutral-800 hover:text-adam-neutral-200" title="New Chat">
            <Plus className="h-4 w-4" />
          </button>
          <span className="text-[11px] font-semibold tracking-wider text-adam-neutral-400">GIENOX</span>
          {bridgeConnected && (
            <span className="flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] text-emerald-400">
              <Wifi className="h-2.5 w-2.5" /> Connected
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-[10px] text-adam-neutral-500">
          {credits !== null && (
            <span className="flex items-center gap-1 rounded-full bg-adam-blue/10 px-2 py-0.5 font-medium text-adam-blue">
              ⚡ {credits.toLocaleString()}
            </span>
          )}
          {tokenUsage.total > 0 && (
            <span>Context: {tokenUsage.total.toLocaleString()} / {contextWindow.toLocaleString()}</span>
          )}
        </div>
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-3 py-4">
        {isEmpty ? (
          <div className="flex h-full flex-col items-center justify-center">
            <img src="/Gienox-Logo.png" alt="" className="mb-4 w-16 opacity-50" />
            <p className="mb-6 text-sm text-adam-neutral-500">What's good?</p>
            <div className="flex w-full max-w-sm flex-col gap-2">
              {STARTER_PROMPTS.map((sp, i) => (
                <button
                  key={i}
                  onClick={() => sendPrompt(sp.text)}
                  className="flex items-center gap-3 rounded-lg border border-adam-neutral-800 bg-adam-neutral-900/50 px-4 py-3 text-left text-sm text-adam-neutral-200 transition-colors hover:border-adam-neutral-700 hover:bg-adam-neutral-800"
                >
                  <span>{sp.icon}</span>
                  <span>{sp.text}</span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="mx-auto max-w-2xl space-y-4">
            {messages.map((msg, i) => (
              <div key={i} className={msg.role === 'user' ? 'flex justify-end' : ''}>
                {msg.role === 'user' ? (
                  <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-adam-blue/20 px-4 py-2.5 text-sm text-adam-text-primary">
                    {msg.content}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {/* Tool call cards */}
                    {msg.toolCalls?.map((tc) => (
                      <ToolCallCardUI key={tc.id} card={tc} />
                    ))}
                    {/* Text content */}
                    {msg.content && (
                      <div className="text-sm leading-relaxed text-adam-neutral-200 whitespace-pre-wrap">
                        {msg.content}
                        {isStreaming && i === messages.length - 1 && (
                          <span className="inline-block w-1.5 h-4 ml-0.5 align-middle bg-adam-blue animate-pulse" />
                        )}
                      </div>
                    )}
                    {isStreaming && i === messages.length - 1 && !msg.content && (
                      <div className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin text-adam-neutral-500" />
                        <span className="text-xs text-adam-neutral-500">Thinking...</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Selection context */}
      {selection && selection.length > 0 && (
        <div className="border-t border-adam-neutral-800 bg-adam-neutral-900/50 px-3 py-2">
          <p className="text-[10px] font-medium text-adam-neutral-500">SELECTED IN FUSION</p>
          <div className="mt-1 flex flex-wrap gap-1">
            {selection.map((s, i) => (
              <span key={i} className="rounded bg-adam-blue/10 px-2 py-0.5 text-[11px] text-adam-blue">
                {(s.name as string) ?? (s.type as string) ?? 'Entity'}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Input area */}
      <div className="shrink-0 border-t border-adam-neutral-800 bg-adam-bg-dark p-3">
        {/* Attachment previews */}
        {attachments.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-2">
            {attachments.map((att, i) => (
              <div key={i} className="group relative">
                <img src={att} alt="" className="h-16 w-16 rounded-lg border border-adam-neutral-700 object-cover" />
                <button
                  onClick={() => setAttachments(prev => prev.filter((_, j) => j !== i))}
                  className="absolute -right-1 -top-1 hidden h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[8px] text-white group-hover:flex"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="relative rounded-xl border border-adam-neutral-800 bg-adam-neutral-900">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendPrompt(input);
              }
            }}
            placeholder="Start building with Gienox..."
            rows={1}
            className="w-full resize-none bg-transparent px-4 py-3 pr-24 text-sm text-adam-text-primary placeholder-adam-neutral-600 outline-none"
          />
          <div className="absolute bottom-2 right-2 flex items-center gap-1.5">
            {/* Attach file */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.pdf,.svg,.stl,.step,.stp,.3mf,.dxf,.dwg,.iges,.igs,.obj,.fbx"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onload = () => {
                  if (typeof reader.result === 'string') {
                    setAttachments(prev => [...prev, reader.result as string]);
                  }
                };
                reader.readAsDataURL(file);
                e.target.value = '';
              }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="rounded-md p-1.5 text-adam-neutral-500 hover:bg-adam-neutral-800 hover:text-adam-neutral-300"
              title="Attach file"
            >
              <Paperclip className="h-4 w-4" />
            </button>

            {/* Screenshot from Fusion */}
            <button
              onClick={async () => {
                if (!bridgeConnected) return;
                try {
                  const result = await fusionBridge.callTool('get_screenshot_context');
                  if (result && typeof result === 'object' && (result as any).screenshot) {
                    setAttachments(prev => [...prev, (result as any).screenshot]);
                  }
                } catch { /* ignore */ }
              }}
              className={`rounded-md p-1.5 transition-colors ${bridgeConnected ? 'text-adam-neutral-500 hover:bg-adam-neutral-800 hover:text-adam-neutral-300' : 'cursor-not-allowed text-adam-neutral-700'}`}
              title={bridgeConnected ? 'Capture Fusion screenshot' : 'Connect to Fusion to capture'}
              disabled={!bridgeConnected}
            >
              <Camera className="h-4 w-4" />
            </button>
            {/* Model selector */}
            <div className="relative">
              <button
                onClick={() => setShowModelPicker(!showModelPicker)}
                className="flex items-center gap-1 rounded-md bg-adam-neutral-800 px-2 py-1 text-[10px] text-adam-neutral-400 hover:text-adam-neutral-200"
              >
                {model === 'auto' && autoTier ? `Auto · ${autoTier}` : MODEL_LABELS[model]}
                <ChevronDown className="h-3 w-3" />
              </button>
              {showModelPicker && (
                <div className="absolute bottom-8 right-0 z-50 w-44 rounded-lg border border-adam-neutral-700 bg-adam-bg-dark py-1 shadow-xl">
                  {Object.entries(MODEL_LABELS).map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => { setModel(key as FusionModel); setShowModelPicker(false); }}
                      className={`w-full px-3 py-1.5 text-left text-xs transition-colors hover:bg-adam-neutral-800 ${model === key ? 'text-adam-blue' : 'text-adam-neutral-300'}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Send button */}
            <button
              onClick={() => sendPrompt(input)}
              disabled={!input.trim() || isStreaming}
              className="rounded-lg bg-adam-blue p-1.5 text-white transition-colors hover:bg-adam-blue/80 disabled:opacity-30"
            >
              {isStreaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {/* Bottom bar with model info */}
        {modelUsed && (
          <div className="mt-1.5 flex items-center justify-between text-[10px] text-adam-neutral-600">
            <span>Model: {MODEL_LABELS[modelUsed] ?? modelUsed}</span>
            {tokenUsage.total > 0 && (
              <span>Token Usage ({Math.round((tokenUsage.total / contextWindow) * 100)}%) {tokenUsage.total.toLocaleString()} / {contextWindow.toLocaleString()}</span>
            )}
          </div>
        )}
      </div>
      </div>{/* end main panel */}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tool Call Card Component
// ---------------------------------------------------------------------------

function ToolCallCardUI({ card }: { card: ToolCallCard }) {
  const [expanded, setExpanded] = useState(false);

  const statusIcon = {
    pending: '⏳',
    running: '⚙️',
    done: '✓',
    error: '✗',
  }[card.status];

  const statusColor = {
    pending: 'text-adam-neutral-500',
    running: 'text-adam-blue',
    done: 'text-emerald-400',
    error: 'text-red-400',
  }[card.status];

  return (
    <div className="rounded-lg border border-adam-neutral-800 bg-adam-neutral-900/50 text-xs">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left"
      >
        <span className={statusColor}>{statusIcon}</span>
        <span className="font-mono text-adam-neutral-300">{card.tool}</span>
        {card.status === 'running' && <Loader2 className="h-3 w-3 animate-spin text-adam-blue" />}
        <span className="ml-auto">
          {expanded ? <ChevronDown className="h-3 w-3 text-adam-neutral-600" /> : <ChevronRight className="h-3 w-3 text-adam-neutral-600" />}
        </span>
      </button>
      {expanded && (
        <div className="border-t border-adam-neutral-800 px-3 py-2">
          <p className="mb-1 text-[10px] font-medium text-adam-neutral-500">PARAMS</p>
          <pre className="overflow-x-auto text-[10px] text-adam-neutral-400">{JSON.stringify(card.params, null, 2)}</pre>
          {card.result != null ? (
            <>
              <p className="mb-1 mt-2 text-[10px] font-medium text-emerald-500">RESULT</p>
              <pre className="overflow-x-auto text-[10px] text-emerald-300/70">{JSON.stringify(card.result, null, 2)}</pre>
            </>
          ) : null}
          {card.error && (
            <>
              <p className="mb-1 mt-2 text-[10px] font-medium text-red-400">ERROR</p>
              <pre className="overflow-x-auto text-[10px] text-red-300/70">{String(card.error)}</pre>
            </>
          )}
        </div>
      )}
    </div>
  );
}
